import React from 'react';
import { Button, Icon } from '@iq/react-components';
import { USER_EVENT_ACTIVITY, USER_EVENT_STATUSES } from '../../../../constants';
import config from '../../../../config';
import { getGroupedUserEventOptions } from '../utils';

const quickFilterTitle = (properties) => (
  <div className="quick-filter-title">
    <label> {properties.title}</label>
    <Button
      design="text"
      slim
      icon={
        <Icon
          icon="he-info"
          size="s"
        />
      }
      tooltip={
        "Selecting 'Quick Filter' resets all other filters to default and applies the filter to all events."
      }
      onClick={(event) => event.preventDefault()}
    ></Button>
  </div>
);

const falseAlarmFilterTitle = (properties) => (
  <div className="false-alarm-filter-title">
    <label> {properties.title}</label>
    <Button
      design="text"
      slim
      icon={
        <Icon
          icon="he-info"
          size="s"
        />
      }
      tooltip={
        'Turning the False alarm filtering on will hide events labeled as "false" from the list and timeline view.'
      }
      onClick={(event) => event.preventDefault()}
    ></Button>
  </div>
);

const getUserEventFiltersSchema = (userEventTypes, tags, users) => {
  const groupedOptions = getGroupedUserEventOptions(userEventTypes);

  const includesTaskTypeEvent = userEventTypes.find((eventType) => !!eventType.task);

  const schema = {
    type: 'object',
    properties: {
      quickFilterTitle: {
        type: 'object',
        title: 'Quick Filter',
      },
      quickFilter: {
        type: 'string',
        default: 'none',
        anyOf: [
          { enum: ['none'], title: 'None' },
          { enum: ['overdue'], title: 'All overdue' },
          { enum: ['todo'], title: 'All To-do' },
        ],
      },
      ...(!config.onPremise && {
        assignee: {
          type: 'string',
          title: 'Assignee',
          isMulti: true,
          uniqueItems: true,
          placeholder: 'Select assignee...',
          default: [],
          anyOf: users.map((user) => ({ enum: [user.id], title: user.name })),
        },
        creator: {
          type: 'string',
          title: 'Creator',
          isMulti: true,
          uniqueItems: true,
          placeholder: 'Select creator...',
          default: [],
          anyOf: users.map((user) => ({ enum: [user.id], title: user.name })),
        },
      }),
      type: {
        type: 'string',
        title: 'Event Type',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select types...',
        groupedOptions,
        default: [],
        anyOf: userEventTypes.map((type) => ({
          enum: [type.id],
          title: type.name,
        })),
      },
      activity: {
        type: 'string',
        title: 'Activity',
        placeholder: 'Select activity...',
        default: 'all',
        anyOf: USER_EVENT_ACTIVITY.map((activity) => ({
          enum: [activity.toLowerCase()],
          title: activity,
        })),
      },
      status: {
        type: 'string',
        title: 'Status',
        placeholder: 'Select status...',
        default: 'all',
        anyOf: USER_EVENT_STATUSES.map((status) => ({
          enum: [status.toLowerCase()],
          title: status,
        })),
      },
      tags: {
        title: 'Tags',
        type: 'string',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select tags...',
        default: [],
        anyOf: tags.map((tag) => ({
          enum: [tag.id],
          title: tag.name,
        })),
      },
    },
  };

  if (!includesTaskTypeEvent) {
    delete schema.properties.activity;
  }

  return schema;
};

const machEventFiltersSchema = (machEventTypes = [], pointGroups = []) => {
  const schema = {
    type: 'object',
    properties: {
      eventType: {
        type: 'string',
        title: 'Event Type',
        isMulti: true,
        uniqueItems: true,
        default: [],
        placeholder: 'Select types...',
        anyOf: machEventTypes.map((machEventType) => ({
          enum: [machEventType.id],
          title: machEventType.name,
        })),
      },
    },
  };

  if (pointGroups.length > 0) {
    schema.properties.remoteSource = {
      type: 'string',
      title: 'Remote Source',
      isMulti: true,
      uniqueItems: true,
      placeholder: 'Select remote sources...',
      default: [],
      anyOf: pointGroups.map((pointGroup) => ({
        enum: [pointGroup],
        title: pointGroup,
      })),
    };
  }
  return schema;
};

const dtmpEventFiltersSchema = (severities = [], algorithmTypes = []) => {
  const schema = {
    type: 'object',
    properties: {
      eventType: {
        type: 'string',
        title: 'Type',
        isMulti: true,
        uniqueItems: true,
        placeholder: 'Select types...',
        default: [],
        anyOf: severities.map((severity) => ({
          enum: [severity.id],
          title: severity.name,
        })),
      },
    },
  };

  if (algorithmTypes.length > 0) {
    schema.properties.algorithmType = {
      type: 'string',
      title: 'Algorithm Type',
      isMulti: true,
      uniqueItems: true,
      placeholder: 'Select algorithm types...',
      default: [],
      anyOf: algorithmTypes.map((type) => ({
        enum: [type],
        title: type,
      })),
    };
  }

  return schema;
};

const getUserEventFiltersUiSchema = (formData) => ({
  quickFilterTitle: {
    'ui:TitleFieldTemplate': quickFilterTitle,
  },
  quickFilter: {
    'ui:options': { label: false },
  },
  status: {
    'ui:disabled': ['overdue', 'todo'].includes(formData?.user?.quickFilter),
  },
});

const machEventFiltersUiSchema = {
  falseAlarmFilterTitle: {
    'ui:TitleFieldTemplate': falseAlarmFilterTitle,
  },
  falseAlarmFilter: {
    'ui:options': { label: false },
  },
  remoteSource: {
    'ui:options': {
      classNames: 'remote-source-select',
    },
    'ui:widget': 'select',
  },
};

const dtmpEventFiltersUiSchema = {
  algorithmType: {
    'ui:options': {
      classNames: 'algorithm-type-select',
    },
    'ui:widget': 'select',
  },
  eventType: {
    'ui:options': {
      classNames: 'event-type-select',
    },
    'ui:widget': 'select',
  },
};

export {
  getUserEventFiltersSchema,
  getUserEventFiltersUiSchema,
  machEventFiltersSchema,
  dtmpEventFiltersSchema,
  machEventFiltersUiSchema,
  dtmpEventFiltersUiSchema,
};
